import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { useKidsStore } from '../../store/kidsStore';
import { toast } from 'sonner';

// ─── Virtual canvas dimensions ────────────────────────────────────────────────
const VW = 800;
const VH = 460;
const JUNCTION_HIT_RADIUS = 32; // click detection radius (virtual px)
const TRAIN_W = 38;
const TRAIN_H = 18;
const TRACK_W = 10;
const COLLISION_DIST = 38; // virtual px

// ─── Colors ───────────────────────────────────────────────────────────────────
const PALETTE: Record<string, string> = {
  red:    '#E74C3C',
  blue:   '#3498DB',
  green:  '#27AE60',
  yellow: '#F1C40F',
  orange: '#E67E22',
};

// ─── Types ────────────────────────────────────────────────────────────────────
interface GNode {
  id: number;
  type: 'entry' | 'junction' | 'station';
  x: number;
  y: number;
  color?: string;
  outputs?: [number, number]; // edge ids for state 0 and state 1
  state: number;
  locked: number; // ms locked (Einstein mechanic)
}

interface GEdge {
  id: number;
  from: number;
  to: number;
}

interface Train {
  id: number;
  color: string;
  edgeId: number;
  t: number;       // 0..1 progress along current edge
  speed: number;   // t units per ms
  express: boolean;
  state: 'moving' | 'done';
}

interface SpawnEvt {
  at: number;      // ms from level start
  color: string;
  entry: number;   // entry node id
  express: boolean;
}

interface GS {
  nodes: GNode[];
  edges: GEdge[];
  trains: Train[];
  entryEdges: Record<number, number>;
  spawnQueue: SpawnEvt[];
  spawnIndex: number;
  elapsed: number;
  score: number;
  combo: number;
  lives: number;
  delivered: number;
  total: number;
  phase: 'idle' | 'playing' | 'victory' | 'defeat';
  trainIdCounter: number;
  particles: Particle[];
}

interface Particle {
  x: number; y: number;
  vx: number; vy: number;
  life: number;   // 0..1
  color: string;
  size: number;
}

// ─── Level configuration ──────────────────────────────────────────────────────
const LEVELS = {
  sinapsis: {
    nombre: 'Sinapsis', icono: '⚡',
    speed: 0.00016, interval: 3800, totalTrains: 12,
    colors: ['red', 'blue'],
    entries: [0], expressChance: 0, lives: 5,
    colorEntryMap: {} as Record<string, number>,
    layoutId: 'sinapsis',
  },
  cerebro: {
    nombre: 'Cerebro', icono: '🧩',
    speed: 0.00024, interval: 4200, totalTrains: 16,
    colors: ['red', 'blue', 'green'],
    entries: [0], expressChance: 0, lives: 4,
    colorEntryMap: {} as Record<string, number>,
    layoutId: 'cerebro',
  },
  genio: {
    nombre: 'Genio', icono: '💡',
    speed: 0.00034, interval: 3000, totalTrains: 20,
    colors: ['red', 'blue', 'green', 'yellow'],
    entries: [0, 1], expressChance: 0.3, lives: 3,
    colorEntryMap: { red: 0, yellow: 1 } as Record<string, number>,
    layoutId: 'genio',
  },
  einstein: {
    nombre: 'Einstein', icono: '🎓',
    speed: 0.00046, interval: 2000, totalTrains: 25,
    colors: ['red', 'blue', 'green', 'yellow', 'orange'],
    entries: [0, 1], expressChance: 0.45, lives: 3,
    colorEntryMap: { red: 0, orange: 0, yellow: 1 } as Record<string, number>,
    layoutId: 'einstein',
  },
};
type NivelKey = keyof typeof LEVELS;

// ─── Layouts ──────────────────────────────────────────────────────────────────

function makeLayout(
  rawNodes: Omit<GNode, 'state' | 'locked'>[],
  edges: GEdge[],
  entryEdges: Record<number, number>,
) {
  return {
    nodes: rawNodes.map(n => ({ ...n, state: 0, locked: 0 })) as GNode[],
    edges,
    entryEdges,
  };
}

// Sinapsis: 2 colors, 1 junction, 1 entry
const LAYOUT_SINAPSIS = makeLayout(
  [
    { id: 0, type: 'entry',   x: 80,  y: 230 },
    { id: 1, type: 'junction',x: 420, y: 230, outputs: [1, 2] as [number,number] },
    { id: 2, type: 'station', x: 700, y: 120, color: 'red' },
    { id: 3, type: 'station', x: 700, y: 340, color: 'blue' },
  ],
  [
    { id: 0, from: 0, to: 1 },
    { id: 1, from: 1, to: 2 },
    { id: 2, from: 1, to: 3 },
  ],
  { 0: 0 },
);

// Cerebro: 3 colors, 3 junctions, 1 entry
// Routes: jct1→(jct2|jct3), jct2→(red|blue), jct3→(blue|green)
const LAYOUT_CEREBRO = makeLayout(
  [
    { id: 0, type: 'entry',   x: 60,  y: 230 },
    { id: 1, type: 'junction',x: 220, y: 230, outputs: [1, 2] as [number,number] },
    { id: 2, type: 'junction',x: 420, y: 120, outputs: [3, 4] as [number,number] },
    { id: 3, type: 'junction',x: 420, y: 340, outputs: [5, 6] as [number,number] },
    { id: 4, type: 'station', x: 680, y: 60,  color: 'red' },
    { id: 5, type: 'station', x: 680, y: 230, color: 'blue' },
    { id: 6, type: 'station', x: 680, y: 400, color: 'green' },
  ],
  [
    { id: 0, from: 0, to: 1 },
    { id: 1, from: 1, to: 2 },
    { id: 2, from: 1, to: 3 },
    { id: 3, from: 2, to: 4 },
    { id: 4, from: 2, to: 5 },
    { id: 5, from: 3, to: 5 },
    { id: 6, from: 3, to: 6 },
  ],
  { 0: 0 },
);

// Genio: 4 colors, 5 junctions, 2 entries
// Red only reachable from entry0, Yellow only from entry1
// Blue and Green reachable from both
const LAYOUT_GENIO = makeLayout(
  [
    { id: 0,  type: 'entry',   x: 60,  y: 120 },
    { id: 1,  type: 'entry',   x: 60,  y: 340 },
    { id: 2,  type: 'junction',x: 210, y: 120, outputs: [2, 3] as [number,number] },
    { id: 3,  type: 'junction',x: 210, y: 340, outputs: [4, 5] as [number,number] },
    { id: 4,  type: 'junction',x: 400, y: 70,  outputs: [6, 7] as [number,number] },
    { id: 5,  type: 'junction',x: 400, y: 230, outputs: [8, 9] as [number,number] },
    { id: 6,  type: 'junction',x: 400, y: 390, outputs: [10,11] as [number,number] },
    { id: 7,  type: 'station', x: 650, y: 50,  color: 'red' },
    { id: 8,  type: 'station', x: 650, y: 185, color: 'blue' },
    { id: 9,  type: 'station', x: 650, y: 325, color: 'green' },
    { id: 10, type: 'station', x: 650, y: 420, color: 'yellow' },
  ],
  [
    { id: 0,  from: 0,  to: 2  },
    { id: 1,  from: 1,  to: 3  },
    { id: 2,  from: 2,  to: 4  },
    { id: 3,  from: 2,  to: 5  },
    { id: 4,  from: 3,  to: 5  },
    { id: 5,  from: 3,  to: 6  },
    { id: 6,  from: 4,  to: 7  },
    { id: 7,  from: 4,  to: 8  },
    { id: 8,  from: 5,  to: 8  },
    { id: 9,  from: 5,  to: 9  },
    { id: 10, from: 6,  to: 9  },
    { id: 11, from: 6,  to: 10 },
  ],
  { 0: 0, 1: 1 },
);

// Einstein: 5 colors, 6 junctions, 2 entries + junction lock mechanic
// Red/Orange from entry0, Yellow from entry1, Blue/Green from both
const LAYOUT_EINSTEIN = makeLayout(
  [
    { id: 0,  type: 'entry',   x: 60,  y: 100 },
    { id: 1,  type: 'entry',   x: 60,  y: 360 },
    { id: 2,  type: 'junction',x: 200, y: 100, outputs: [2, 3]   as [number,number] },
    { id: 3,  type: 'junction',x: 200, y: 360, outputs: [4, 5]   as [number,number] },
    { id: 4,  type: 'junction',x: 380, y: 55,  outputs: [6, 7]   as [number,number] },
    { id: 5,  type: 'junction',x: 380, y: 230, outputs: [8, 9]   as [number,number] },
    { id: 6,  type: 'junction',x: 380, y: 405, outputs: [10, 11] as [number,number] },
    { id: 7,  type: 'junction',x: 560, y: 320, outputs: [12, 13] as [number,number] },
    { id: 8,  type: 'station', x: 700, y: 40,  color: 'red'    },
    { id: 9,  type: 'station', x: 700, y: 160, color: 'orange' },
    { id: 10, type: 'station', x: 700, y: 270, color: 'blue'   },
    { id: 11, type: 'station', x: 700, y: 370, color: 'green'  },
    { id: 12, type: 'station', x: 700, y: 430, color: 'yellow' },
  ],
  [
    { id: 0,  from: 0,  to: 2  },
    { id: 1,  from: 1,  to: 3  },
    { id: 2,  from: 2,  to: 4  },
    { id: 3,  from: 2,  to: 5  },
    { id: 4,  from: 3,  to: 5  },
    { id: 5,  from: 3,  to: 6  },
    { id: 6,  from: 4,  to: 8  },
    { id: 7,  from: 4,  to: 9  },
    { id: 8,  from: 5,  to: 9  },
    { id: 9,  from: 5,  to: 10 },
    { id: 10, from: 6,  to: 10 },
    { id: 11, from: 6,  to: 7  },
    { id: 12, from: 7,  to: 11 },
    { id: 13, from: 7,  to: 12 },
  ],
  { 0: 0, 1: 1 },
);

function getLayout(nivel: NivelKey) {
  switch (nivel) {
    case 'cerebro':  return LAYOUT_CEREBRO;
    case 'genio':    return LAYOUT_GENIO;
    case 'einstein': return LAYOUT_EINSTEIN;
    default:         return LAYOUT_SINAPSIS;
  }
}

// ─── Pure helpers ─────────────────────────────────────────────────────────────

function findNode(gs: GS, id: number): GNode {
  return gs.nodes.find(n => n.id === id)!;
}
function findEdge(gs: GS, id: number): GEdge {
  return gs.edges.find(e => e.id === id)!;
}

function getTrainVirtualPos(gs: GS, train: Train): { x: number; y: number } {
  const edge = findEdge(gs, train.edgeId);
  const from = findNode(gs, edge.from);
  const to   = findNode(gs, edge.to);
  return {
    x: from.x + (to.x - from.x) * train.t,
    y: from.y + (to.y - from.y) * train.t,
  };
}

function generateSpawns(
  total: number,
  interval: number,
  colors: string[],
  entries: number[],
  colorEntryMap: Record<string, number>,
  expressChance: number,
): SpawnEvt[] {
  // Build a shuffled pool ensuring equal color distribution
  const pool: string[] = [];
  for (let i = 0; i < total; i++) pool.push(colors[i % colors.length]);
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }

  const spawns: SpawnEvt[] = [];
  let time = 1200;
  for (let i = 0; i < total; i++) {
    const color = pool[i];
    const forcedEntry = colorEntryMap[color];
    const entry = forcedEntry !== undefined
      ? forcedEntry
      : entries[Math.floor(Math.random() * entries.length)];
    spawns.push({ at: time, color, entry, express: Math.random() < expressChance });
    time += interval;
  }
  return spawns;
}

function drawRoundRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function NexoGame() {
  const tenant = useTenant();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedKid, registerProgress, registerPlayTime } = useKidsStore();

  const nivel = ((searchParams.get('nivel') as NivelKey) || 'sinapsis');
  const config = LEVELS[nivel] || LEVELS.sinapsis;

  // Refs
  const canvasRef      = useRef<HTMLCanvasElement>(null);
  const containerRef   = useRef<HTMLDivElement>(null);
  const gsRef          = useRef<GS>(null!);
  const rafRef         = useRef<number>(0);
  const lastTimeRef    = useRef<number>(0);
  const secondsRef     = useRef<number>(0);

  // UI state
  const [score,     setScore]     = useState(0);
  const [lives,     setLives]     = useState(5);
  const [combo,     setCombo]     = useState(0);
  const [delivered, setDelivered] = useState(0);
  const [total,     setTotal]     = useState(0);
  const [phase,     setPhase]     = useState<GS['phase']>('idle');
  const [seconds,   setSeconds]   = useState(0);
  const [canvasW,   setCanvasW]   = useState(0);
  const [canvasH,   setCanvasH]   = useState(0);

  // ── Canvas sizing ──
  useEffect(() => {
    const measure = () => {
      if (!containerRef.current) return;
      setCanvasW(containerRef.current.clientWidth);
      setCanvasH(containerRef.current.clientHeight);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // ── Timer ──
  useEffect(() => {
    if (phase !== 'playing') return;
    const id = setInterval(() => {
      secondsRef.current += 1;
      setSeconds(secondsRef.current);
    }, 1000);
    return () => clearInterval(id);
  }, [phase]);

  // ── Sync ref state → React UI ──
  const syncUI = useCallback(() => {
    const gs = gsRef.current;
    if (!gs) return;
    setScore(gs.score);
    setLives(gs.lives);
    setCombo(gs.combo);
    setDelivered(gs.delivered);
    if (gs.phase !== 'playing') setPhase(gs.phase);
  }, []);

  // ── Save progress ──
  const saveProgress = useCallback(async (gs: GS) => {
    if (!selectedKid) return;
    try {
      await registerProgress(selectedKid.id, {
        juego_id: null,
        tipo_nivel: 'nexo',
        nivel_id: Object.keys(LEVELS).indexOf(nivel) + 1,
        tablero_id: null,
        tiempo: secondsRef.current,
        movimientos: gs.delivered,
      });
      await registerPlayTime(selectedKid.id, Math.ceil(secondsRef.current / 60));
      if (gs.phase === 'victory') toast.success('¡Progreso guardado! 🎉');
    } catch {
      // silent
    }
  }, [selectedKid, nivel, registerProgress, registerPlayTime]);

  // ── Start game ──
  const startGame = useCallback(() => {
    const layout = getLayout(nivel);
    const spawns = generateSpawns(
      config.totalTrains, config.interval,
      config.colors, config.entries,
      config.colorEntryMap, config.expressChance,
    );
    const gs: GS = {
      nodes: layout.nodes.map(n => ({ ...n, state: 0, locked: 0 })),
      edges: layout.edges,
      trains: [],
      entryEdges: layout.entryEdges,
      spawnQueue: spawns,
      spawnIndex: 0,
      elapsed: 0,
      score: 0,
      combo: 0,
      lives: config.lives,
      delivered: 0,
      total: config.totalTrains,
      phase: 'playing',
      trainIdCounter: 0,
      particles: [],
    };
    gsRef.current = gs;
    secondsRef.current = 0;
    lastTimeRef.current = 0;
    setScore(0); setLives(config.lives); setCombo(0);
    setDelivered(0); setTotal(config.totalTrains);
    setSeconds(0); setPhase('playing');
  }, [nivel, config]);

  // ── Update logic ──
  const updateRef = useRef<(dt: number) => void>(null!);
  updateRef.current = (dt: number) => {
    const gs = gsRef.current;
    if (!gs || gs.phase !== 'playing') return;
    gs.elapsed += dt;

    // Junction locks
    for (const n of gs.nodes) {
      if (n.locked > 0) n.locked = Math.max(0, n.locked - dt);
    }

    // Particle decay
    for (const p of gs.particles) {
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.life -= dt / 600;
    }
    gs.particles = gs.particles.filter(p => p.life > 0);

    // Spawn trains
    while (gs.spawnIndex < gs.spawnQueue.length &&
           gs.spawnQueue[gs.spawnIndex].at <= gs.elapsed) {
      const evt = gs.spawnQueue[gs.spawnIndex++];
      const firstEdge = gs.entryEdges[evt.entry];
      if (firstEdge !== undefined) {
        gs.trains.push({
          id: gs.trainIdCounter++,
          color: evt.color,
          edgeId: firstEdge,
          t: 0,
          speed: evt.express ? config.speed * 1.7 : config.speed,
          express: evt.express,
          state: 'moving',
        });
      }
    }

    // Move trains
    const toRemove = new Set<number>();
    for (const train of gs.trains) {
      if (train.state !== 'moving') { toRemove.add(train.id); continue; }
      train.t += train.speed * dt;

      if (train.t >= 1) {
        const edge = findEdge(gs, train.edgeId);
        const node = findNode(gs, edge.to);

        if (node.type === 'station') {
          if (node.color === train.color) {
            gs.delivered++;
            const mult = Math.min(1 + Math.floor(gs.combo / 3) * 0.5, 3.0);
            gs.score += Math.round((train.express ? 150 : 100) * mult);
            gs.combo++;
            spawnParticles(gs, node.x, node.y, PALETTE[train.color]);
          } else {
            gs.score = Math.max(0, gs.score - 50);
            gs.lives = Math.max(0, gs.lives - 1);
            gs.combo = 0;
            spawnParticles(gs, node.x, node.y, '#aaaaaa');
          }
          train.state = 'done';
          toRemove.add(train.id);
          syncUI();
        } else if (node.type === 'junction') {
          const nextEdge = node.outputs![node.state];
          train.edgeId = nextEdge;
          train.t = 0;
        } else {
          train.t = 0; // entry node (fallback)
        }
      }
    }

    // Collision detection (distance in virtual coords)
    const moving = gs.trains.filter(t => t.state === 'moving' && !toRemove.has(t.id));
    for (let i = 0; i < moving.length; i++) {
      if (toRemove.has(moving[i].id)) continue;
      for (let j = i + 1; j < moving.length; j++) {
        if (toRemove.has(moving[j].id)) continue;
        const pa = getTrainVirtualPos(gs, moving[i]);
        const pb = getTrainVirtualPos(gs, moving[j]);
        if (Math.hypot(pa.x - pb.x, pa.y - pb.y) < COLLISION_DIST) {
          toRemove.add(moving[i].id);
          toRemove.add(moving[j].id);
          gs.score = Math.max(0, gs.score - 80);
          gs.lives = Math.max(0, gs.lives - 1);
          gs.combo = 0;
          spawnParticles(gs, (pa.x + pb.x) / 2, (pa.y + pb.y) / 2, '#ff4444');
          syncUI();
        }
      }
    }

    gs.trains = gs.trains.filter(t => !toRemove.has(t.id));

    // End conditions
    if (gs.lives <= 0) {
      gs.phase = 'defeat';
      syncUI();
      saveProgress(gs);
    } else if (gs.spawnIndex >= gs.spawnQueue.length && gs.trains.length === 0) {
      gs.phase = 'victory';
      syncUI();
      saveProgress(gs);
    }
  };

  // ── Render ──
  const renderRef = useRef<() => void>(null!);
  renderRef.current = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const gs = gsRef.current;

    const cw = canvas.width;
    const ch = canvas.height;
    const scale = Math.min(cw / VW, ch / VH);
    const ox = (cw - VW * scale) / 2;
    const oy = (ch - VH * scale) / 2;

    const toC = (vx: number, vy: number) => ({
      x: vx * scale + ox,
      y: vy * scale + oy,
    });

    // Background
    ctx.fillStyle = '#0f1020';
    ctx.fillRect(0, 0, cw, ch);

    if (!gs) return;

    // ── Draw tracks ──
    for (const edge of gs.edges) {
      const fn = findNode(gs, edge.from);
      const tn = findNode(gs, edge.to);
      const f = toC(fn.x, fn.y);
      const t = toC(tn.x, tn.y);

      // Shadow
      ctx.strokeStyle = 'rgba(0,0,0,0.5)';
      ctx.lineWidth = (TRACK_W + 3) * scale;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(f.x, f.y + 2 * scale);
      ctx.lineTo(t.x, t.y + 2 * scale);
      ctx.stroke();

      // Rail fill
      ctx.strokeStyle = '#334';
      ctx.lineWidth = TRACK_W * scale;
      ctx.beginPath();
      ctx.moveTo(f.x, f.y);
      ctx.lineTo(t.x, t.y);
      ctx.stroke();

      // Rail highlight
      ctx.strokeStyle = '#556';
      ctx.lineWidth = (TRACK_W - 4) * scale;
      ctx.beginPath();
      ctx.moveTo(f.x, f.y);
      ctx.lineTo(t.x, t.y);
      ctx.stroke();

      // Sleepers (crosshatch marks)
      const dx = tn.x - fn.x;
      const dy = tn.y - fn.y;
      const len = Math.hypot(dx, dy);
      const steps = Math.floor(len / 30);
      const nx = -dy / len;
      const ny =  dx / len;
      ctx.strokeStyle = '#445';
      ctx.lineWidth = 2 * scale;
      for (let s = 1; s < steps; s++) {
        const p = s / steps;
        const mx = fn.x + dx * p;
        const my = fn.y + dy * p;
        const c = toC(mx, my);
        const sl = 6 * scale;
        ctx.beginPath();
        ctx.moveTo(c.x + nx * sl, c.y + ny * sl);
        ctx.lineTo(c.x - nx * sl, c.y - ny * sl);
        ctx.stroke();
      }
    }

    // ── Draw stations ──
    for (const node of gs.nodes) {
      if (node.type !== 'station') continue;
      const pos = toC(node.x, node.y);
      const color = PALETTE[node.color!] || '#888';
      const rw = 36 * scale;
      const rh = 26 * scale;

      // Glow
      const grd = ctx.createRadialGradient(pos.x, pos.y, 0, pos.x, pos.y, rw * 1.5);
      grd.addColorStop(0, color + '44');
      grd.addColorStop(1, 'transparent');
      ctx.fillStyle = grd;
      ctx.fillRect(pos.x - rw * 1.5, pos.y - rh * 1.5, rw * 3, rh * 3);

      // Body
      ctx.fillStyle = color;
      ctx.strokeStyle = '#ffffff88';
      ctx.lineWidth = 2 * scale;
      drawRoundRect(ctx, pos.x - rw, pos.y - rh, rw * 2, rh * 2, 6 * scale);
      ctx.fill();
      ctx.stroke();

      // Roof (darker shade)
      ctx.fillStyle = 'rgba(0,0,0,0.25)';
      drawRoundRect(ctx, pos.x - rw, pos.y - rh, rw * 2, rh * 0.6, 6 * scale);
      ctx.fill();

      // Label
      ctx.fillStyle = '#fff';
      ctx.font = `bold ${13 * scale}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('▣', pos.x, pos.y);
    }

    // ── Draw entries ──
    for (const node of gs.nodes) {
      if (node.type !== 'entry') continue;
      const pos = toC(node.x, node.y);
      const s = 14 * scale;

      ctx.fillStyle = '#667';
      ctx.strokeStyle = '#889';
      ctx.lineWidth = 1.5 * scale;
      // Arrow shape
      ctx.beginPath();
      ctx.moveTo(pos.x - s, pos.y - s * 0.5);
      ctx.lineTo(pos.x + s * 0.4, pos.y - s * 0.5);
      ctx.lineTo(pos.x + s * 0.4, pos.y - s);
      ctx.lineTo(pos.x + s, pos.y);
      ctx.lineTo(pos.x + s * 0.4, pos.y + s);
      ctx.lineTo(pos.x + s * 0.4, pos.y + s * 0.5);
      ctx.lineTo(pos.x - s, pos.y + s * 0.5);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }

    // ── Draw junctions ──
    for (const node of gs.nodes) {
      if (node.type !== 'junction') continue;
      const pos = toC(node.x, node.y);
      const r = 18 * scale;
      const isLocked = node.locked > 0;

      // Glow ring
      ctx.strokeStyle = isLocked ? '#ff444488' : '#f0c04088';
      ctx.lineWidth = 5 * scale;
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, r + 4 * scale, 0, Math.PI * 2);
      ctx.stroke();

      // Body
      ctx.fillStyle = isLocked ? '#aa3333' : '#e8b800';
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2 * scale;
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();

      // Direction arrow toward active output destination
      if (node.outputs) {
        const activeEdgeId = node.outputs[node.state];
        const ae = gs.edges.find(e => e.id === activeEdgeId);
        if (ae) {
          const dest = findNode(gs, ae.to);
          const dp = toC(dest.x, dest.y);
          const dx = dp.x - pos.x;
          const dy = dp.y - pos.y;
          const len = Math.hypot(dx, dy);
          const ux = dx / len;
          const uy = dy / len;
          const arrowLen = r * 0.7;
          const ax = pos.x + ux * arrowLen;
          const ay = pos.y + uy * arrowLen;

          ctx.strokeStyle = '#333';
          ctx.lineWidth = 2.5 * scale;
          ctx.lineCap = 'round';
          ctx.beginPath();
          ctx.moveTo(pos.x - ux * arrowLen * 0.4, pos.y - uy * arrowLen * 0.4);
          ctx.lineTo(ax, ay);
          ctx.stroke();

          // Arrowhead
          const angle = Math.atan2(uy, ux);
          const al = 5 * scale;
          ctx.beginPath();
          ctx.moveTo(ax, ay);
          ctx.lineTo(ax - al * Math.cos(angle - 0.5), ay - al * Math.sin(angle - 0.5));
          ctx.moveTo(ax, ay);
          ctx.lineTo(ax - al * Math.cos(angle + 0.5), ay - al * Math.sin(angle + 0.5));
          ctx.stroke();
        }
      }

      // Lock timer indicator
      if (isLocked) {
        ctx.fillStyle = '#fff';
        ctx.font = `bold ${9 * scale}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(Math.ceil(node.locked / 1000).toString(), pos.x, pos.y);
      }
    }

    // ── Draw trains ──
    for (const train of gs.trains) {
      if (train.state !== 'moving') continue;
      const edge = findEdge(gs, train.edgeId);
      const fn = findNode(gs, edge.from);
      const tn = findNode(gs, edge.to);

      const vx = fn.x + (tn.x - fn.x) * train.t;
      const vy = fn.y + (tn.y - fn.y) * train.t;
      const pos = toC(vx, vy);
      const angle = Math.atan2(tn.y - fn.y, tn.x - fn.x);
      const color = PALETTE[train.color] || '#888';

      const tw = TRAIN_W * scale;
      const th = TRAIN_H * scale;

      ctx.save();
      ctx.translate(pos.x, pos.y);
      ctx.rotate(angle);

      // Shadow
      ctx.fillStyle = 'rgba(0,0,0,0.4)';
      drawRoundRect(ctx, -tw / 2 + 2 * scale, -th / 2 + 3 * scale, tw, th, 4 * scale);
      ctx.fill();

      // Body
      ctx.fillStyle = color;
      ctx.strokeStyle = '#ffffffcc';
      ctx.lineWidth = 1.5 * scale;
      drawRoundRect(ctx, -tw / 2, -th / 2, tw, th, 4 * scale);
      ctx.fill();
      ctx.stroke();

      // Cab (front brighter section)
      ctx.fillStyle = 'rgba(255,255,255,0.2)';
      drawRoundRect(ctx, tw / 2 - 10 * scale, -th / 2, 10 * scale, th, 4 * scale);
      ctx.fill();

      // Express indicator
      if (train.express) {
        ctx.fillStyle = '#FFD700';
        ctx.font = `bold ${8 * scale}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('⚡', 0, 0);
      }

      ctx.restore();
    }

    // ── Particles ──
    for (const p of gs.particles) {
      const ppos = toC(p.x, p.y);
      ctx.globalAlpha = p.life;
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(ppos.x, ppos.y, p.size * scale * p.life, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  };

  // ── Game loop ──
  useEffect(() => {
    if (phase !== 'playing') return;

    const loop = (ts: number) => {
      const dt = lastTimeRef.current
        ? Math.min(ts - lastTimeRef.current, 100)
        : 16;
      lastTimeRef.current = ts;
      updateRef.current(dt);
      renderRef.current();
      if (gsRef.current?.phase === 'playing') {
        rafRef.current = requestAnimationFrame(loop);
      } else {
        renderRef.current(); // final frame
      }
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [phase]);

  // ── Click handler ──
  const handleCanvasClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const gs = gsRef.current;
    if (!gs || gs.phase !== 'playing') return;
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const scale = Math.min(canvas.width / VW, canvas.height / VH);
    const ox = (canvas.width - VW * scale) / 2;
    const oy = (canvas.height - VH * scale) / 2;
    const vx = (e.clientX - rect.left - ox) / scale;
    const vy = (e.clientY - rect.top  - oy) / scale;

    for (const node of gs.nodes) {
      if (node.type !== 'junction') continue;
      if (Math.hypot(node.x - vx, node.y - vy) < JUNCTION_HIT_RADIUS) {
        if (node.locked > 0) return;
        node.state = node.state === 0 ? 1 : 0;
        if (nivel === 'einstein') node.locked = 2000;
        break;
      }
    }
  }, [nivel]);

  const handleCanvasTouch = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const gs = gsRef.current;
    if (!gs || gs.phase !== 'playing') return;
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const scale = Math.min(canvas.width / VW, canvas.height / VH);
    const ox = (canvas.width - VW * scale) / 2;
    const oy = (canvas.height - VH * scale) / 2;
    const touch = e.changedTouches[0];
    const vx = (touch.clientX - rect.left - ox) / scale;
    const vy = (touch.clientY - rect.top  - oy) / scale;

    for (const node of gs.nodes) {
      if (node.type !== 'junction') continue;
      if (Math.hypot(node.x - vx, node.y - vy) < JUNCTION_HIT_RADIUS) {
        if (node.locked > 0) return;
        node.state = node.state === 0 ? 1 : 0;
        if (nivel === 'einstein') node.locked = 2000;
        break;
      }
    }
  }, [nivel]);

  const comboMult = Math.min(1 + Math.floor(combo / 3) * 0.5, 3.0);
  const formatTime = (s: number) =>
    `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div
      className="h-full flex flex-col"
      style={{ background: tenant.theme.colors.gradients.main, fontFamily: tenant.theme.fonts.body.family }}
    >
      {/* Top bar */}
      <div className="bg-black/20 backdrop-blur-sm px-3 py-2 flex items-center gap-2 flex-wrap shrink-0">
        <span className="text-white font-bold text-base whitespace-nowrap">🚂 Nexo</span>
        <div className="w-px h-5 bg-white/30 hidden sm:block" />
        <div className="flex gap-1 flex-wrap">
          {(Object.entries(LEVELS) as [NivelKey, typeof LEVELS.sinapsis][]).map(([key, lv]) => (
            <button
              key={key}
              onClick={() => { setSearchParams({ nivel: key }); setPhase('idle'); }}
              className={`px-2 py-0.5 rounded text-xs font-semibold transition-all ${
                nivel === key ? 'bg-white' : 'bg-white/20 text-white hover:bg-white/30'
              }`}
              style={nivel === key ? { color: tenant.theme.colors.primary } : {}}
            >
              {lv.icono} {lv.nombre}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        {/* Stats */}
        <div className="flex items-center gap-3 text-white text-xs font-semibold">
          <span>⏱ {formatTime(seconds)}</span>
          <span>🚂 {delivered}/{total}</span>
          <span style={{ color: '#f0c040' }}>✨ {score}</span>
          {combo >= 3 && (
            <span style={{ color: '#ff6b6b' }}>🔥 ×{comboMult.toFixed(1)}</span>
          )}
          <span>
            {Array.from({ length: config.lives }).map((_, i) => (
              <span key={i} className={i >= lives ? 'opacity-20' : ''}>❤️</span>
            ))}
          </span>
        </div>
      </div>

      {/* Canvas area */}
      <div ref={containerRef} className="flex-1 relative overflow-hidden">
        <canvas
          ref={canvasRef}
          width={canvasW}
          height={canvasH}
          onClick={handleCanvasClick}
          onTouchStart={handleCanvasTouch}
          className="absolute inset-0"
          style={{ cursor: phase === 'playing' ? 'pointer' : 'default', touchAction: 'none' }}
        />

        {/* Idle overlay */}
        {phase === 'idle' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-6">
            <div className="text-center">
              <div className="text-6xl mb-2">🚂</div>
              <h2
                className="text-3xl font-bold text-white drop-shadow-lg mb-1"
                style={{ fontFamily: tenant.theme.fonts.primary.family }}
              >
                Nexo
              </h2>
              <p className="text-white/80 text-sm max-w-xs text-center px-4">
                Haz clic en los desvíos para dirigir los trenes a su estación del mismo color.
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 text-white/90 text-xs max-w-xs text-center">
              <p className="font-semibold mb-1">{config.icono} {config.nombre}</p>
              <p>{config.colors.length} colores · {config.lives} vidas · {config.totalTrains} trenes</p>
            </div>
            <button
              onClick={startGame}
              className="px-10 py-4 rounded-2xl font-bold text-lg shadow-xl text-white transition-transform hover:scale-105"
              style={{ background: tenant.theme.colors.primary }}
            >
              🎮 Iniciar
            </button>
          </div>
        )}

        {/* Victory modal */}
        {phase === 'victory' && (
          <div className="absolute inset-0 bg-black/70 flex items-center justify-center p-4 z-10">
            <div className="bg-white rounded-3xl p-10 text-center max-w-sm w-full shadow-2xl" style={{ animation: 'bounceIn 0.5s' }}>
              <div className="text-7xl mb-3">🎉</div>
              <h2
                className="text-4xl font-bold mb-4"
                style={{ fontFamily: tenant.theme.fonts.primary.family, color: tenant.theme.colors.primary }}
              >
                ¡Completado!
              </h2>
              <div className="text-gray-700 text-base space-y-1 mb-6">
                <p>Trenes entregados: <strong>{delivered}/{total}</strong></p>
                <p>Puntuación: <strong>{score}</strong></p>
                <p>Tiempo: <strong>{formatTime(seconds)}</strong></p>
                <p>Vidas restantes: <strong>{'❤️'.repeat(lives)}</strong></p>
              </div>
              <button
                onClick={startGame}
                className="px-8 py-3 rounded-2xl font-bold text-white text-lg shadow-lg hover:scale-105 transition-transform"
                style={{ background: tenant.theme.colors.primary }}
              >
                🔄 Jugar de Nuevo
              </button>
            </div>
          </div>
        )}

        {/* Defeat modal */}
        {phase === 'defeat' && (
          <div className="absolute inset-0 bg-black/70 flex items-center justify-center p-4 z-10">
            <div className="bg-white rounded-3xl p-10 text-center max-w-sm w-full shadow-2xl" style={{ animation: 'bounceIn 0.5s' }}>
              <div className="text-7xl mb-3">😢</div>
              <h2
                className="text-4xl font-bold mb-4"
                style={{ fontFamily: tenant.theme.fonts.primary.family, color: '#e74c3c' }}
              >
                ¡Sin vidas!
              </h2>
              <div className="text-gray-700 text-base space-y-1 mb-6">
                <p>Entregados: <strong>{delivered}/{total}</strong></p>
                <p>Puntuación: <strong>{score}</strong></p>
                <p>Tiempo: <strong>{formatTime(seconds)}</strong></p>
              </div>
              <button
                onClick={startGame}
                className="px-8 py-3 rounded-2xl font-bold text-white text-lg shadow-lg hover:scale-105 transition-transform"
                style={{ background: tenant.theme.colors.primary }}
              >
                🔄 Intentar de Nuevo
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes bounceIn {
          0%   { transform: scale(0.3); opacity: 0; }
          60%  { transform: scale(1.05); opacity: 1; }
          100% { transform: scale(1); }
        }
      `}</style>
    </div>
  );
}

// ─── Particle helper ──────────────────────────────────────────────────────────
function spawnParticles(gs: GS, vx: number, vy: number, color: string) {
  for (let i = 0; i < 8; i++) {
    const angle = (Math.PI * 2 * i) / 8;
    gs.particles.push({
      x: vx, y: vy,
      vx: Math.cos(angle) * 0.06,
      vy: Math.sin(angle) * 0.06,
      life: 1,
      color,
      size: 4 + Math.random() * 4,
    });
  }
}
